/**
 * Projects an imported `ReportSpec` onto the studio shelves; the spec is authoritative on import and
 * the user edits from there. Dimensions match on identity rather than spelling: the deck writes
 * `CostCenter` where the dataset binding says `Cost Center`.
 */

import type { ReportSpec, ReportFilter, RowAxis } from 'src/components/star-grid/api/reportEngineTypes';
import type {
  StudioBinding,
  StudioCatalog,
  StudioDataset,
  StudioDeck,
  StudioField,
  StudioLineEntry,
  StudioLineSet
} from 'src/components/star-grid/reportStudio.types';
import { studioField } from 'src/components/star-grid/utils/studioDraft';
import { IMPORTED_LINE_SET_VERSION, importedRowField, importedRowLineSet, stripStrSuffix } from 'src/components/star-grid/utils/importedRowLineSet';

/** Lower-case, strip anything that is not a letter or digit, and drop the deck's `FPA Deck` prefix. */
/**
 * The deck's display name and the dataset's CDP name reduced to one token.
 *
 * `FPA Deck Country` and `fpa_deck_country` already meet after the prefix is stripped, but the LINE
 * ITEM dimension does not: the deck calls it `FPA Deck Line Item` and the dataset calls it
 * `pnl_statement_line`. That single mismatch dropped every Channel and Allocation filter on import,
 * so the shelf was empty and the save had nothing to write back. Mirrors the statement-line aliases
 * in the service's `saved_row_adapter`.
 */
const STATEMENT_LINE_TOKENS = new Set(['pnlstatementline', 'statementline', 'lineitem', 'pnllineitem']);

export const normalizeDimension = (name: string): string => {
  const token = String(name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/^fpadeck/, '');
  return STATEMENT_LINE_TOKENS.has(token) ? 'lineitem' : token;
};

/**
 * The binding for a (dimension, hierarchy) pair, preferring an exact hierarchy match.
 *
 * A dimension can carry several hierarchies — `CostCenter` has CostCenter / Type / VP — and they are
 * different roll-ups of the same members, so picking the wrong one changes every row.
 */
export const findBinding = (bindings: readonly StudioBinding[], dimension: string, hierarchy?: string): StudioBinding | undefined => {
  const wantDimension = normalizeDimension(dimension);
  const candidates = bindings.filter((binding) => normalizeDimension(binding.dimension) === wantDimension);
  if (candidates.length === 0) return undefined;
  if (!hierarchy) return candidates[0];
  const wantHierarchy = normalizeDimension(hierarchy);
  return candidates.find((binding) => normalizeDimension(binding.hierarchy) === wantHierarchy) ?? candidates[0];
};

/** Namespaces a stand-in binding id so it can never collide with a real rollup's. */
export const SPEC_FILTER_BINDING_PREFIX = 'spec-filter:';

/**
 * A stand-in binding built from what the spec's own filter states.
 *
 * `rollup` and `tree_id` are deliberately absent: there is no hierarchy to fetch members from, and
 * claiming one would make the member picker issue a request that can only 404. The chip therefore
 * displays its members but cannot browse the tree — visibly degraded, which is the point.
 */
const specFilterBinding = (filter: ReportFilter): StudioBinding => ({
  id: `${SPEC_FILTER_BINDING_PREFIX}${normalizeDimension(filter.dimension)}`,
  label: filter.dimension,
  dimension: filter.dimension,
  hierarchy: filter.hierarchy ?? filter.dimension,
  tree_id: null,
  subsets: [],
  attributes: []
});

/**
 * Stand-in bindings for the spec filters the dataset cannot resolve, in spec order.
 *
 * A spec filter whose dimension has no dataset binding used to be dropped, so a saved report whose
 * spec demonstrably carried filters opened with an EMPTY shelf — nothing to see, nothing to edit,
 * and the next save wrote the filters away for good. `findBinding` matches on dimension, so the
 * second filter for an already-stood-in dimension finds it and no duplicate is made.
 */
export const specFilterBindings = (dataset: StudioDataset, spec: ReportSpec): StudioBinding[] => {
  const standIns: StudioBinding[] = [];
  for (const filter of spec.filters ?? []) {
    if (findBinding([...dataset.bindings, ...standIns], filter.dimension, filter.hierarchy)) continue;
    standIns.push(specFilterBinding(filter));
  }
  return standIns;
};

/**
 * `dataset` plus the stand-ins the spec's own filters need, so the shelf can look up every chip.
 *
 * Stand-ins stay OUT of `selectable_binding_ids`: they are not real rollups, so the palette must not
 * offer them and the field editor must keep showing its "hierarchy unavailable" warning on them.
 */
export const studioDatasetWithSpecFilters = (dataset: StudioDataset, standIns: readonly StudioBinding[]): StudioDataset =>
  standIns.length === 0
    ? dataset
    : {
        ...dataset,
        bindings: [...dataset.bindings, ...standIns],
        selectable_binding_ids: dataset.selectable_binding_ids ?? dataset.bindings.map((binding) => binding.id)
      };

/** `catalog` with the stand-ins on every dataset, so validation and the shelf resolve the same ids. */
export const studioCatalogWithSpecFilters = (catalog: StudioCatalog, standIns: readonly StudioBinding[]): StudioCatalog =>
  standIns.length === 0 ? catalog : { ...catalog, datasets: catalog.datasets.map((dataset) => studioDatasetWithSpecFilters(dataset, standIns)) };

/**
 * The deck with its rows and filters replaced by the spec's.
 *
 * Columns are left alone — they come from the deck/scenario selection, not from the spec.
 */
export const studioDeckFromSpec = (deck: StudioDeck, dataset: StudioDataset, spec: ReportSpec): StudioDeck => {
  // Stand-ins are for the FILTERS shelf only: a row shelf entry drives the whole row axis, and one
  // backed by a binding with no member tree would render a report with no rows rather than a gap.
  const filterBindings = [...dataset.bindings, ...specFilterBindings(dataset, spec)];
  const rowBinding = findBinding(dataset.bindings, spec.row_axis?.dimension ?? '', spec.row_axis?.hierarchy);
  const filterFields = (spec.filters ?? [])
    .map((filter) => ({ filter, binding: findBinding(filterBindings, filter.dimension, filter.hierarchy) }))
    .filter((entry): entry is { filter: (typeof entry)['filter']; binding: StudioBinding } => Boolean(entry.binding))
    // The same dimension can appear twice across a screen's own filters; one chip per dimension.
    .filter((entry, index, all) => all.findIndex((other) => other.binding.id === entry.binding.id) === index)
    // Members carried over, not just the binding: `studioField` makes an UNRESTRICTED chip, so the
    // spec's members were dropped on import ("All members") and then dropped again on save, because
    // a member-less filter restricts nothing and is not written back.
    .map(({ binding, filter }) => {
      const field = studioField(binding.id);
      const members = [...(filter.members ?? [])];
      return members.length > 0 ? { ...field, selection: { kind: 'members' as const, members } } : field;
    });
  // The row axis becomes a LINE SET, not a bare field. `studioField` defaults to "All members", which
  // threw away the spec's own ordered member list (221 rows on WW Report) -- so an imported Prophecy
  // report showed "All members" in the shelf, and because `StudioFieldEditor` routes the pencil to the
  // line-set editor only for `kind === 'line_set'`, editing a row opened the filter-style member modal
  // instead of the imported report's rows.
  const rowSet = rowBinding ? importedRowLineSet(spec, spec.name || rowBinding.label || rowBinding.id) : undefined;
  const nested = nestedRows(dataset, spec);
  if (rowSet && rowBinding) {
    // REUSE the set the rows shelf already points at (the studio template ships one, "Statement
    // layout") rather than registering a second. Appending left the template's default set in
    // `line_sets` AND rendering first in the line-set editor, so after an import you saw two sets and
    // the top one was the stale template rows -- which reads as though the import had produced them.
    // Its id and label are kept so the editor's heading stays stable; only the ENTRIES become the
    // imported report's.
    const existing = deck.line_sets.find((item) => deck.rows.some((row) => row.selection.reference_id === item.id));
    const entries = [...rowSet.entries, ...(nested?.lines ?? [])];
    const target = {
      ...(existing ? { ...existing, editable: true } : rowSet),
      binding_id: rowBinding.id,
      entries,
      ...savedExpansion(spec.row_axis)
    };
    return {
      ...deck,
      rows: [importedRowField(target), ...(nested ? [nestedRowField(nested.binding, target)] : [])],
      line_sets: [...deck.line_sets.filter((item) => item.id !== target.id), target],
      filters: filterFields
    };
  }
  if (rowBinding && nested && nested.lines.length > 0) {
    const set: StudioLineSet = {
      id: `imported-nested-${nested.binding.id}`,
      label: nested.binding.label,
      version: IMPORTED_LINE_SET_VERSION,
      binding_id: nested.binding.id,
      editable: true,
      entries: nested.lines,
      ...savedExpansion(spec.row_axis)
    };
    return {
      ...deck,
      rows: [studioField(rowBinding.id), nestedRowField(nested.binding, set)],
      line_sets: [...deck.line_sets.filter((item) => item.id !== set.id), set],
      filters: filterFields
    };
  }
  return {
    ...deck,
    rows: rowBinding ? [studioField(rowBinding.id), ...(nested ? [studioField(nested.binding.id)] : [])] : deck.rows,
    filters: filterFields
  };
};

/** Both axes' saved expansion merged onto the one set their lines share. */
const savedExpansion = (
  axis: RowAxis | undefined
): Pick<StudioLineSet, 'expand_default' | 'expanded_members' | 'collapsed_members' | 'expandable_members'> => {
  const expandDefault = axis?.expand_default ?? axis?.nested?.expand_default;
  const explicit = Array.isArray(axis?.expandable_members) || Array.isArray(axis?.nested?.expandable_members);
  // A spec that stated neither reopens unconfigured, so the line set and the report stay as they were.
  if (expandDefault === undefined && !explicit) return {};
  return {
    ...(expandDefault !== undefined
      ? {
          expand_default: expandDefault,
          expanded_members: [...(axis?.expanded_members ?? []), ...(axis?.nested?.expanded_members ?? [])],
          collapsed_members: [...(axis?.collapsed_members ?? []), ...(axis?.nested?.collapsed_members ?? [])]
        }
      : {}),
    ...(explicit ? { expandable_members: [...(axis?.expandable_members ?? []), ...(axis?.nested?.expandable_members ?? [])] } : {})
  };
};

/** Whether the spec's second row dimension can be a Rows field: both it and the first must resolve, or it is read-only. */
export const isNestedRowsPlaceable = (dataset: StudioDataset, spec: Pick<ReportSpec, 'row_axis'>): boolean => {
  const axis = spec.row_axis;
  return Boolean(
    axis?.nested &&
      findBinding(dataset.bindings, axis.dimension, axis.hierarchy) &&
      findBinding(dataset.bindings, axis.nested.dimension, axis.nested.hierarchy)
  );
};

/** The placeable second row dimension: its binding, and one line per member naming it so the save reads it back. */
const nestedRows = (
  dataset: StudioDataset,
  spec: Pick<ReportSpec, 'row_axis'>
): { readonly binding: StudioBinding; readonly lines: StudioLineEntry[] } | undefined => {
  const nested: RowAxis | undefined = spec.row_axis?.nested;
  const binding = nested && isNestedRowsPlaceable(dataset, spec) ? findBinding(dataset.bindings, nested.dimension, nested.hierarchy) : undefined;
  if (!nested || !binding) return undefined;
  const members = (nested.selector?.elements ?? []).filter((member): member is string => typeof member === 'string');
  return {
    binding,
    lines: members.map((member, index) => ({
      id: `imported-nested-line-${index}`,
      kind: 'member',
      label: stripStrSuffix(member),
      member_id: member,
      binding_id: binding.id
    }))
  };
};

/** The second rows field, pointing at the shared line set that holds its lines. */
const nestedRowField = (binding: StudioBinding, set: StudioLineSet): StudioField => ({
  id: `imported-nested-row-${binding.id}`,
  binding_id: binding.id,
  selection: { kind: 'line_set', reference_id: set.id, members: [] }
});
